import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminContributionService } from '../../services/contributionService';
import { ContributionStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TABS = ['All Plans', 'Plan Advance Queue'];

function AllPlansTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-contribution-plans'],
    queryFn: () => adminContributionService.getPlans().then(r => r.data.data),
  });
  const plans = data?.results || [];

  if (isLoading) return <LoadingSpinner />;
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Reference</th><th>User</th><th>Goal</th><th>Target</th><th>Balance</th><th>Cycle</th><th>Status</th><th>Started</th>
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No contribution plans yet.</td></tr>
            ) : plans.map(p => (
              <tr key={p.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.reference}</td>
                <td>{p.user_name}</td>
                <td style={{ fontWeight: 600 }}>{p.goal_name}</td>
                <td>{formatCurrency(p.goal_amount)}</td>
                <td style={{ fontWeight: 600, color: '#00d9a5' }}>{formatCurrency(p.balance)}</td>
                <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{p.cycle}</td>
                <td><ContributionStatusBadge status={p.status} /></td>
                <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(p.start_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlanAdvanceQueueTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['pending-plan-advances'],
    queryFn: () => adminContributionService.getPendingPlanAdvances().then(r => r.data.data),
  });
  const advances = data?.results || [];

  const executeMutation = useMutation({
    mutationFn: (id) => adminContributionService.executePlanAdvance(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-plan-advances'] }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (advances.length === 0) return <div className="card"><p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '1rem' }}>No pending Plan Advances.</p></div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Reference</th><th>Remaining Goal</th><th>Fee</th><th>Payout</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {advances.map(a => (
              <tr key={a.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.reference}</td>
                <td>{formatCurrency(a.remaining_goal_amount)}</td>
                <td style={{ color: '#F5A623' }}>{formatCurrency(a.fee_amount)}</td>
                <td style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(a.payout_amount)}</td>
                <td><ContributionStatusBadge status={a.status} /></td>
                <td>
                  <button
                    className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                    disabled={executeMutation.isPending}
                    onClick={() => executeMutation.mutate(a.id)}
                  >
                    Execute
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>
        Execute is only available once the linked approval request has been approved by a checker in the Approval Queue.
      </p>
    </div>
  );
}

export default function ContributionPlansAdmin() {
  const [tab, setTab] = useState(0);
  return (
    <div>
      <div className="page-header">
        <h1>Savings Plans</h1>
        <p>Individual contribution plans and Plan Advance approvals</p>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: 'var(--surface)', borderRadius: 12, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === i ? 700 : 400,
            background: tab === i ? 'linear-gradient(135deg, rgba(21,150,224,0.2), rgba(21,150,224,0.08))' : 'transparent',
            color: tab === i ? '#1596E0' : 'var(--text-2)',
            transition: 'all .15s',
          }}>
            {t}
          </button>
        ))}
      </div>
      {tab === 0 ? <AllPlansTab /> : <PlanAdvanceQueueTab />}
    </div>
  );
}
